import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { Tooltip } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

import styles from './PersonDetails.module.css';
import { getApplicationStatus } from '../../../../api/lendingAPI/ApplicationSelector';
import { useIsInSelfServeSplitTest } from '../../../../customHooks/useIsInSelfServeSplitTest';
import { DateInput } from '../../../../forms/dateInput/index';
import { FormField } from '../../../../forms/formField/FormField';
import { NameField } from '../../../../forms/nameField/index';
import companyPositionMessages from '../../../../helpers/companyPositions.messages';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import messages from '../PersonModal.messages';

const selector = formValueSelector('person');
const mapStateToProps = (state: RootState) => ({
    ...state.personModal,
    hasCreditDecision: getApplicationStatus(state) !== 'open',
    isGuarantor: selector(state, 'isGuarantor')
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const PersonDetails = (props: PropsFromRedux) => {
    const intl = useIntl();
    const isInSelfServeSplitTest = useIsInSelfServeSplitTest();

    const { hasCreditDecision, isEditingPerson, isGuarantor } = props;
    const personalDetailsEditingDisabled =
        hasCreditDecision && isEditingPerson && isGuarantor;
    return (
        <>
            <NameField
                name="full_name"
                label={<FormattedMessage {...messages.fullName} />}
                disabled={personalDetailsEditingDisabled}
            />
            <DateInput
                name="dateOfBirth"
                label={<FormattedMessage {...messages.dateOfBirth} />}
                disabled={personalDetailsEditingDisabled}
            />
            <label>
                <FormattedMessage {...messages.roleTitle} />
            </label>
            <FormField
                className={styles.checkboxGroup}
                name="isDirector"
                type="checkbox"
                label={
                    <FormattedMessage {...companyPositionMessages.director} />
                }
            />
            <FormField
                className={styles.checkboxGroup}
                name="isShareholder"
                type="checkbox"
                label={
                    <FormattedMessage
                        {...companyPositionMessages.shareholder}
                    />
                }
            />
            <DEOnly>
                <div>
                    <div>
                        <label>
                            <FormattedMessage
                                id="_.person.additional_guarantor"
                                defaultMessage="Additional Guarantor"
                            />
                        </label>
                        <FormField
                            name="isGuarantor"
                            type="checkbox"
                            data-testid="isGuarantorDE"
                            label={
                                <FormattedMessage
                                    id="_.person.add_additional_guarantor"
                                    defaultMessage="I also want to add this person as a guarantor"
                                />
                            }
                        />
                    </div>
                    {isGuarantor ? (
                        <div className="inline-block rounded-m bg-secondary-90 p-xl">
                            <FormattedMessage
                                id="_.person.additional_guarantor_consent_msg"
                                defaultMessage="In adding this person as a guarantor, I consent to the checking of their personal creditworthiness"
                            />
                            <FormField
                                className={styles.checkboxGroup}
                                name="additionalGuarantorConsent"
                                type="checkbox"
                                label={
                                    <FormattedMessage
                                        id="_.person.consent_confirmation"
                                        defaultMessage="I consent"
                                    />
                                }
                            />
                        </div>
                    ) : null}
                </div>
            </DEOnly>
            {getCountry() === 'UK' ? (
                <div>
                    <div
                        className={styles.guarantorCheckbox}
                        data-tip
                        data-for="contactTooltip"
                    >
                        <FormField
                            className={styles.checkboxGroup}
                            name="isGuarantor"
                            data-testid="isGuarantor"
                            type="checkbox"
                            disabled={hasCreditDecision}
                            label={
                                <FormattedMessage
                                    {...companyPositionMessages.guarantor}
                                />
                            }
                        />
                        {hasCreditDecision && !isInSelfServeSplitTest ? (
                            <Tooltip
                                content={intl.formatMessage(
                                    messages.contactToolTip
                                )}
                                placement="right"
                                data-testid="contactTooltip"
                            />
                        ) : null}
                    </div>

                    {isGuarantor ? (
                        <div
                            className={styles.privacyPolicy}
                            data-testid="privacyPolicy"
                        >
                            <FormattedMessage
                                {...messages.privacyPolicy}
                                values={{
                                    privacyPolicyLink: (
                                        <a
                                            href="https://www.iwoca.co.uk/privacy-policy/"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <FormattedMessage
                                                {...messages.privacyPolicyLink}
                                            />
                                        </a>
                                    )
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(PersonDetails);
